import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Button,
} from '@mui/material';
import {
  AdminPanelSettings,
  Assignment,
  ShoppingCart,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import SiteWrapper from '../../home-pages/SiteWrapper';
import { useFirebaseAuthentication } from 'lib/firebase';
import { getLevelConfig } from '../../../utils/levelConfig';
import { getStoreConfigAsync } from '../../../utils/storeConfig';
import { loadAllUserPoints } from './utils/adminUtils';

// Import extracted components
import PointManagement from './components/PointManagement';
import LevelConfiguration from './components/LevelConfiguration';
import StoreConfiguration from './components/StoreConfiguration';
import PendingPurchases from './components/PendingPurchases';

const AdminPanel = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const user = useFirebaseAuthentication();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [userPoints, setUserPoints] = useState({});
  const [levelConfig, setLevelConfig] = useState(getLevelConfig());
  const [storeConfig, setStoreConfig] = useState([]);
  const [storeConfigLoading, setStoreConfigLoading] = useState(true);

  // Load user points data
  const loadUserPoints = useCallback(async () => {
    try {
      const pointsData = await loadAllUserPoints();
      setUserPoints(pointsData);
    } catch (error) {
      enqueueSnackbar('Error loading user points', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  // Load store configuration data
  const loadStoreConfig = useCallback(async () => {
    try {
      setStoreConfigLoading(true);
      const configData = await getStoreConfigAsync();
      setStoreConfig(configData);
    } catch (error) {
      enqueueSnackbar('Error loading store configuration', { variant: 'error' });
    } finally {
      setStoreConfigLoading(false);
    }
  }, [enqueueSnackbar]);

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        loadUserPoints(),
        loadStoreConfig()
      ]);
      setLoading(false);
    };
    
    loadData();
  }, [loadUserPoints, loadStoreConfig]);

  // Handlers for component updates
  const handlePointsUpdated = useCallback(async () => {
    await loadUserPoints();
  }, [loadUserPoints]);

  const handleLevelConfigUpdated = useCallback(() => {
    const newConfig = getLevelConfig();
    setLevelConfig(newConfig);
    loadUserPoints(); // Reload to update level calculations
  }, [loadUserPoints]);

  const handleStoreConfigUpdated = useCallback(async () => {
    await loadStoreConfig();
  }, [loadStoreConfig]);

  // Loading state
  if (loading) {
    return (
      <SiteWrapper>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
        >
          <CircularProgress size={60} />
        </Box>
      </SiteWrapper>
    );
  }

  return (
    <SiteWrapper>
      <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', py: 4 }}>
        <Container maxWidth="xl">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Box
              sx={{
                textAlign: 'center',
                mb: 6,
                py: 4,
                px: 3,
                background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
                borderRadius: 4,
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <AdminPanelSettings sx={{ fontSize: 80, mb: 2, opacity: 0.9 }} />
              <Typography
                variant={isMobile ? 'h4' : 'h3'}
                fontWeight="bold"
                gutterBottom
                sx={{ textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}
              >
                Admin Dashboard
              </Typography>
              <Typography
                variant={isMobile ? 'body1' : 'h6'}
                sx={{ opacity: 0.9, maxWidth: 600, mx: 'auto', mb: 3 }}
              >
                Manage user points, configure levels, oversee store items, and approve purchases
              </Typography>
              
              {/* Navigation Buttons */}
              <Box 
                sx={{ 
                  display: 'flex', 
                  gap: 2, 
                  justifyContent: 'center',
                  flexWrap: 'wrap'
                }}
              >
                <Button
                  variant="contained"
                  startIcon={<Assignment />}
                  onClick={() => navigate('/chores')}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    borderRadius: 3,
                    px: 3,
                    py: 1,
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.3)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.2s',
                  }}
                >
                  View Chores
                </Button>
                <Button
                  variant="contained"
                  startIcon={<ShoppingCart />}
                  onClick={() => navigate('/chorestore')}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    borderRadius: 3,
                    px: 3,
                    py: 1,
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.3)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.2s',
                  }}
                >
                  View Store
                </Button>
              </Box>
            </Box>
          </motion.div>

          <Grid container spacing={4}>
            <Grid item xs={12}>
              {/* Point Management Section */}
              <PointManagement 
                userPoints={userPoints}
                onPointsUpdated={handlePointsUpdated}
              />
            </Grid>

            <Grid item xs={12}>
              {/* Level Configuration Section */}
              <LevelConfiguration 
                levelConfig={levelConfig}
                onLevelConfigUpdated={handleLevelConfigUpdated}
              />
            </Grid>

            <Grid item xs={12}>
              {/* Store Configuration Section */}
              <StoreConfiguration 
                storeConfig={storeConfig}
                storeConfigLoading={storeConfigLoading}
                onStoreConfigUpdated={handleStoreConfigUpdated}
              />
            </Grid>

            <Grid item xs={12}>
              {/* Pending Purchases Section */}
              <Box sx={{ mt: 4 }}>
                <Typography 
                  variant="h4" 
                  gutterBottom 
                  sx={{ 
                    textAlign: 'center', 
                    fontWeight: 'bold',
                    mb: 3,
                    color: '#2c3e50'
                  }}
                >
                  📋 Pending Purchase Requests
                </Typography>
                <PendingPurchases user={user} />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </SiteWrapper>
  );
};

export default AdminPanel;